//#region src/index.ts
/**
* Event names are either:
* - custom and must be handled by GTM Trigger for Custom Events, matching "^eec\."
* - official when `useOfficialEventNames` is called.
* https://developers.google.com/tag-manager/ecommerce-ga4
*/
let EventNamesSchema = /* @__PURE__ */ function(EventNamesSchema$1) {
	EventNamesSchema$1[EventNamesSchema$1["CustomPrefixedWithEEC"] = 0] = "CustomPrefixedWithEEC";
	EventNamesSchema$1[EventNamesSchema$1["OfficialGA4"] = 1] = "OfficialGA4";
	return EventNamesSchema$1;
}({});
/**
* Typescript class for the class we have in our .NET API.
* https://dev.azure.com/natch/NatchOS/_git/natchos?path=%2Fsrc%2FNatchOS.Domain%2FAnalytics%2FDataLayerProduct.cs&_a=contents&version=GBmaster
*/
var NatchOsDataLayerProduct = class {};
var NatchOsDataLayerTransactionDetails = class {};
var Ga4Product = class {};
var NatchGtm = class {
	/**
	* @param useOfficialEventNames Use false for "eec." event names (i.e. eec.addToCart); use true for official GA4 event names (i.e. add_to_cart)
	*/
	constructor(eventNamesSchema) {
		this.eventNameViewItemList = "eec.impression";
		this.eventNameSelectItem = "eec.clickImpression";
		this.eventNameViewItem = "eec.detail";
		this.eventNameAddToWishlist = "eec.addtoWishlist";
		this.eventNameAddToCart = "eec.addToCart";
		this.eventNameRemoveFromCart = "eec.removeFromCart";
		this.eventNameCartView = "eec.cart";
		this.eventNameCartBeginCheckout = "eec.cartBeginCheckout";
		this.eventNameCartPaymentInfo = "eec.cartPaymentInfo";
		this.eventNameCartShippingInfo = "eec.cartShippingInfo";
		this.eventNamePurchase = "eec.purchase";
		this.eventNameViewPromotion = "view_promotion";
		this.eventNameSelectPromotion = "select_promotion";
		if (eventNamesSchema == EventNamesSchema.OfficialGA4) {
			this.eventNameViewItemList = "view_item_list";
			this.eventNameSelectItem = "select_item";
			this.eventNameViewItem = "view_item";
			this.eventNameAddToWishlist = "add_to_wishlist";
			this.eventNameAddToCart = "add_to_cart";
			this.eventNameRemoveFromCart = "remove_from_cart";
			this.eventNameCartView = "view_cart";
			this.eventNameCartBeginCheckout = "begin_checkout";
			this.eventNameCartPaymentInfo = "add_payment_info";
			this.eventNameCartShippingInfo = "add_shipping_info";
			this.eventNamePurchase = "purchase";
			this.eventNameViewPromotion = "view_promotion";
			this.eventNameSelectPromotion = "select_promotion";
		}
	}
	gtmIsLoaded() {
		if (typeof dataLayer === "undefined") return false;
		return typeof dataLayer.find((element) => element["event"] == "gtm.load") !== "undefined";
	}
	/**
	* Processes all `data-datalayer-*` data-attributes on the page.
	* Detail, impressions (on lists), checkout/cart, purchase.
	* Binds event listeners to products in a list (based on data-attributes).
	* Processes custom events on elements using [data-datalayer-custom-event-name].
	* Binds event listeners to links with custom events (based on [data-datalayer-custom-event-name]).
	* You can call this in `document.addEventListener("DOMContentLoaded", () => { });`
	*/
	processPage() {
		if (typeof dataLayer === "undefined") {
			console.warn("dataLayer is not defined.");
			return;
		}
		this.processDetail();
		this.processImpression();
		this.processCheckout();
		this.processPurchase();
		this.processCustomEvents();
		this.attachCustomEventClickHandlerToLinks();
	}
	/**
	* Sends the [[eventNameAddToCart]] event.
	* https://developers.google.com/tag-manager/ecommerce-ga4#add_a_product_to_a_shopping_cart
	* https://developers.google.com/gtagjs/reference/ga4-events#add_to_cart
	* @param productDto Product data.
	* @param eventValue The numeric value for the event, i.e. product price per sales unit.
	* @param beforePush Function to add extra data to the item (Ga4Product), i.e. `item.item_list_name = listName;`
	*/
	trackAddToFavorites(productDto, eventValue, beforePush) {
		if (typeof dataLayer === "undefined") {
			console.warn("dataLayer is not defined.");
			return;
		}
		if (!productDto) {
			console.warn("product is null.");
			return;
		}
		console.debug("trackAddToFavorites", arguments);
		const item = this.convertToGA4(productDto);
		item.quantity = 1;
		if (beforePush) beforePush(item);
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: this.eventNameAddToWishlist,
			ecommerce: {
				currency: eventValue ? this.getCurrencyCode() : null,
				value: eventValue,
				items: [item]
			}
		});
	}
	/**
	* Sends the [[eventNameAddToCart]] event.
	* https://developers.google.com/tag-manager/ecommerce-ga4#add_a_product_to_a_shopping_cart
	* https://developers.google.com/gtagjs/reference/ga4-events#add_to_cart
	* @param productDto Product data.
	* @param eventValue The numeric value for the event, i.e. the total price of the products added to the cart (not per unit but for the specified quantity).
	* @param beforePush Function to add extra data to the item (Ga4Product), i.e. `item.item_list_name = listName;`
	*/
	trackAddToCart(productDto, eventValue, beforePush) {
		if (typeof dataLayer === "undefined") {
			console.warn("dataLayer is not defined.");
			return;
		}
		if (!productDto) {
			console.warn("product is null.");
			return;
		}
		console.debug("trackAddToCart", arguments);
		const item = this.convertToGA4(productDto);
		if (beforePush) beforePush(item);
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: this.eventNameAddToCart,
			ecommerce: {
				currency: eventValue ? this.getCurrencyCode() : null,
				value: eventValue,
				items: [item]
			}
		});
	}
	/**
	* Sends the [[eventNameAddToCart]] event.
	* https://developers.google.com/tag-manager/ecommerce-ga4#add_a_product_to_a_shopping_cart
	* https://developers.google.com/gtagjs/reference/ga4-events#add_to_cart
	* @param productDtoList Product data list.
	* @param eventValue The numeric value for the event, i.e. the total price of the products added to the cart (not per unit but for the specified quantity).
	* @param beforePush Function to add extra data to the item (Ga4Product), i.e. `item.item_list_name = listName;`
	*/
	trackAddListToCart(productDtoList, eventValue, beforePush) {
		if (typeof dataLayer === "undefined") {
			console.warn("dataLayer is not defined.");
			return;
		}
		if (!productDtoList || productDtoList.length === 0) {
			console.warn("product list is null or empty.");
			return;
		}
		console.debug("trackAddListToCart", arguments);
		const items = productDtoList.map((productDto) => this.convertToGA4(productDto));
		if (beforePush) items.forEach((item) => beforePush(item));
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: this.eventNameAddToCart,
			ecommerce: {
				currency: eventValue ? this.getCurrencyCode() : null,
				value: eventValue,
				items
			}
		});
	}
	/**
	* Sends the [[eventNameRemoveFromCart]] event.
	* @param productDto Product data.
	* @param eventValue The numeric value for the event, i.e. the total price of the products removed from the cart (not per unit but for the specified quantity).
	* @param beforePush Function to add extra data to the item (Ga4Product), i.e. `item.item_list_name = listName;`
	*/
	trackRemoveFromCart(productDto, eventValue, beforePush) {
		if (typeof dataLayer === "undefined") {
			console.warn("dataLayer is not defined.");
			return;
		}
		if (!productDto) {
			console.warn("product is null");
			return;
		}
		console.debug("trackRemoveFromCart", arguments);
		const item = this.convertToGA4(productDto);
		if (beforePush) beforePush(item);
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: this.eventNameRemoveFromCart,
			ecommerce: {
				currency: eventValue ? this.getCurrencyCode() : null,
				value: eventValue,
				items: [item]
			}
		});
	}
	/**
	* Sends the [[eventNameRemoveFromCart]] event.
	* @param productDtoList Product data list.
	* @param eventValue The numeric value for the event, i.e. the total price of the products removed from the cart (not per unit but for the specified quantity).
	* @param beforePush Function to add extra data to the item (Ga4Product), i.e. `item.item_list_name = listName;`
	*/
	trackRemoveListFromCart(productDtoList, eventValue, beforePush) {
		if (typeof dataLayer === "undefined") {
			console.warn("dataLayer is not defined.");
			return;
		}
		if (!productDtoList || productDtoList.length === 0) {
			console.warn("product list is null or empty.");
			return;
		}
		console.debug("trackRemoveListFromCart", arguments);
		const items = productDtoList.map((productDto) => this.convertToGA4(productDto));
		if (beforePush) items.forEach((item) => beforePush(item));
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: this.eventNameRemoveFromCart,
			ecommerce: {
				currency: eventValue ? this.getCurrencyCode() : null,
				value: eventValue,
				items
			}
		});
	}
	processCheckout() {
		const cartElement = document.querySelector("[data-datalayer-cart]");
		if (cartElement) {
			const trackCartValue = cartElement.getAttribute("data-datalayer-cart");
			if (trackCartValue !== null) {
				if (trackCartValue.toLowerCase() === "0" || trackCartValue.toLowerCase() === "false") {
					console.log("[data-datalayer-cart] was falsy, skip tracking");
					return;
				}
			}
			this.processCartEvent(cartElement, "data-datalayer-cart-track-view", this.eventNameCartView);
			this.processCartEvent(cartElement, "data-datalayer-cart-track-begin", this.eventNameCartBeginCheckout);
			this.processCartEvent(cartElement, "data-datalayer-cart-track-payment", this.eventNameCartPaymentInfo);
			this.processCartEvent(cartElement, "data-datalayer-cart-track-shipping", this.eventNameCartShippingInfo);
		}
	}
	processPurchase() {
		const transactionItem = document.querySelector("[data-datalayer-transaction]");
		if (transactionItem) {
			const transactionHeader = transactionItem.getAttribute("data-datalayer-transaction");
			if (transactionHeader == null) {
				console.error("[data-datalayer-transaction] value is null");
				return;
			}
			const transactionProducts = transactionItem.getAttribute("data-datalayer-transaction-products");
			if (transactionProducts == null) {
				console.error("[data-datalayer-transaction-products] value is null");
				return;
			}
			const transactionDetails = JSON.parse(transactionHeader);
			const list = JSON.parse(transactionProducts).map((dto) => {
				let item = this.convertToGA4(dto);
				if (typeof item.affiliation === "undefined" || item.affiliation == null) item.affiliation = transactionDetails.affiliation;
				return item;
			});
			dataLayer.push({ ecommerce: null });
			dataLayer.push({
				event: this.eventNamePurchase,
				ecommerce: {
					transaction_id: transactionDetails.id,
					currency: transactionDetails.currency,
					value: transactionDetails._revenue,
					shipping: transactionDetails._shipping,
					tax: transactionDetails._tax,
					items: list
				}
			});
		}
	}
	processCustomEvents() {
		document.querySelectorAll("[data-datalayer-custom-event-name]:not(a)").forEach((element) => {
			const customEventName = element.getAttribute("data-datalayer-custom-event-name") || "";
			const customEventParameter = element.getAttribute("data-datalayer-custom-event-parameter") || "";
			if (customEventName) dataLayer.push({
				event: customEventName,
				customEventParameter
			});
		});
	}
	/**
	* Attach click handler to a[data-datalayer-custom-event-name] elements.
	* Will add an event to the data layer with the value of the attribute.
	* Optionally add a parameter in the [data-datalayer-custom-event-parameter] attribute.
	*/
	attachCustomEventClickHandlerToLinks() {
		if (typeof dataLayer === "undefined") {
			console.warn("dataLayer is not defined.");
			return;
		}
		const linkList = document.querySelectorAll("a[data-datalayer-custom-event-name]");
		for (let linkIndex = 0; linkIndex < linkList.length; linkIndex++) {
			const linkElement = linkList[linkIndex];
			linkElement.addEventListener("click", this.handleCustomEventLink(linkElement));
		}
	}
	processDetail() {
		const detailItem = document.querySelector("[data-datalayer-detail]");
		if (detailItem) {
			const jsonValue = detailItem.getAttribute("data-datalayer-detail");
			if (jsonValue == null) {
				console.error("[data-datalayer-detail] value is null");
				return;
			}
			const dto = JSON.parse(jsonValue);
			const item = this.convertToGA4(dto);
			dataLayer.push({ ecommerce: null });
			dataLayer.push({
				event: this.eventNameViewItem,
				ecommerce: {
					currency: item.price ? this.getCurrencyCode() : null,
					value: item.price,
					items: [item]
				}
			});
			if (item.promotion_id || item.promotion_name) dataLayer.push({
				event: this.eventNameViewPromotion,
				ecommerce: {
					currency: item.price ? this.getCurrencyCode() : null,
					value: item.price,
					items: [item]
				}
			});
		}
	}
	processImpression() {
		const listContainers = document.querySelectorAll("[data-datalayer-list-name]");
		if (listContainers.length > 0) for (let listIndex = 0; listIndex < listContainers.length; listIndex++) {
			const listContainer = listContainers[listIndex];
			const nodeList = listContainer.querySelectorAll("[data-datalayer-list-item]");
			if (nodeList.length <= 0) continue;
			let listName = listContainer.getAttribute("data-datalayer-list-name");
			let listId = listContainer.getAttribute("data-datalayer-list-id");
			const list = [];
			for (let i = 0; i < nodeList.length; i++) {
				const jsonValue = nodeList[i].getAttribute("data-datalayer-list-item") || "";
				const dto = JSON.parse(jsonValue);
				const item = this.convertToGA4(dto);
				item.index = i + 1;
				item.item_list_name = listName;
				item.item_list_id = listId;
				list.push(item);
				const links = nodeList[i].querySelectorAll("[data-datalayer-list-trackclick]");
				this.attachClickEvents(links, item);
			}
			dataLayer.push({ ecommerce: null });
			dataLayer.push({
				event: this.eventNameViewItemList,
				ecommerce: { items: list }
			});
		}
	}
	processCartEvent(cartElement, matchAttributeName, eventName) {
		if (cartElement.getAttribute(matchAttributeName) === null) return;
		const value = cartElement.getAttribute("data-datalayer-cart-value");
		const currency = cartElement.getAttribute("data-datalayer-cart-currency");
		const paymentType = cartElement.getAttribute("data-datalayer-cart-track-payment-method");
		const shippingTier = cartElement.getAttribute("data-datalayer-cart-track-shipping-method");
		const nodeList = cartElement.querySelectorAll("[data-datalayer-cart-item]");
		const list = this.extractProductsFromList(nodeList, "data-datalayer-cart-item");
		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: eventName,
			ecommerce: {
				currency: currency ? currency : this.getCurrencyCode(),
				value,
				payment_type: paymentType,
				shipping_tier: shippingTier,
				items: list
			}
		});
	}
	extractProductsFromList(nodeList, attributeName) {
		const productList = [];
		if (nodeList.length > 0) for (let i = 0; i < nodeList.length; i++) {
			const jsonValue = nodeList[i].getAttribute(attributeName) || "";
			if (jsonValue === "") continue;
			const dto = JSON.parse(jsonValue);
			const item = this.convertToGA4(dto);
			if (typeof item.item_list_id === "undefined" || item.item_list_id == null) item.item_list_id = nodeList[i].getAttribute("data-datalayer-list-id");
			if (typeof item.item_list_name === "undefined" || item.item_list_name == null) item.item_list_name = nodeList[i].getAttribute("data-datalayer-list-name");
			productList.push(item);
		}
		return productList;
	}
	/**
	* Attaches an event to track click impressions.
	* @param linkList List of nodes for the product to track the click.
	* @param listName Name of the list the elements are placed in.
	* @param productDto Product data.
	*/
	attachClickEvents(linkList, item) {
		for (let linkIndex = 0; linkIndex < linkList.length; linkIndex++) {
			var linkElement = linkList[linkIndex];
			linkElement.addEventListener("click", this.handleClickOnListItem(linkElement, item));
		}
	}
	/**
	* Sends the [[eventNameClickImpression]] event.
	* @param linkElement DOM element that was clicked.
	* @param listName Name of the list the element is placed in.
	* @param productDto The product data.
	*/
	handleClickOnListItem(linkElement, item) {
		return (e) => {
			var url = linkElement.getAttribute("href") || "";
			if (!url) return true;
			if (!this.gtmIsLoaded()) return true;
			dataLayer.push({ ecommerce: null });
			if (item.promotion_id) dataLayer.push({
				event: this.eventNameSelectPromotion,
				ecommerce: { items: [item] }
			});
			dataLayer.push({
				event: this.eventNameSelectItem,
				ecommerce: { items: [item] },
				eventCallback: function(containerId) {
					if (containerId.startsWith("GTM-")) document.location.href = url;
				},
				eventTimeout: 2e3
			});
			e.preventDefault();
			return false;
		};
	}
	convertToGA4(dto) {
		const result = new Ga4Product();
		result.item_id = dto.id;
		result.item_name = dto.name;
		result.item_brand = dto.brand;
		result.item_variant = dto.variant;
		result.item_list_id = dto.list_id;
		result.item_list_name = dto.list_name;
		result.promotion_id = dto.promotion_id;
		result.promotion_name = dto.promotion_name;
		result.affiliation = dto.affiliation;
		result.price = dto._price;
		result.quantity = dto._quantity;
		if (dto.category) {
			var categories = (dto.category || "").split("/");
			if (categories.length >= 1) result.item_category = categories[0];
			if (categories.length >= 2) result.item_category2 = categories[1];
			if (categories.length >= 3) result.item_category3 = categories[2];
			if (categories.length >= 4) result.item_category4 = categories[3];
			if (categories.length >= 5) result.item_category5 = categories[4];
		}
		return result;
	}
	getCurrencyCode() {
		var currencyCode = document.body.getAttribute("data-datalayer-currency");
		if (currencyCode == null || currencyCode.length < 1) currencyCode = "EUR";
		return currencyCode;
	}
	handleCustomEventLink(linkElement) {
		return (e) => {
			const url = linkElement.getAttribute("href") || "";
			if (!url) return true;
			if (!this.gtmIsLoaded()) return true;
			const customEventName = linkElement.getAttribute("data-datalayer-custom-event-name") || "";
			const customEventParameter = linkElement.getAttribute("data-datalayer-custom-event-parameter") || "";
			dataLayer.push({
				event: customEventName,
				customEventParameter,
				eventCallback: function() {
					document.location.href = url;
				},
				eventTimeout: 2e3
			});
			e.preventDefault();
			return false;
		};
	}
};

//#endregion
export { EventNamesSchema, Ga4Product, NatchOsDataLayerProduct, NatchOsDataLayerTransactionDetails, NatchGtm as default };
//# sourceMappingURL=index.js.map